import React, { useState, useRef, useEffect } from 'react';
import { Play, Volume2, VolumeX } from 'lucide-react';

// Renders a mixed media gallery (video + images). The first item is expected
// to be the hero video — it autoplays muted to grab attention without sound.
const ProductGallery = ({ items }) => {
  const [active, setActive] = useState(0);
  const [muted, setMuted] = useState(true);
  const videoRefs = useRef({});

  // Reset playback when switching slides
  useEffect(() => {
    const current = items[active];
    if (current?.type === 'video') {
      const v = videoRefs.current[active];
      if (v) {
        v.muted = muted;
        v.play().catch(() => {});
      }
    }
    // pause any other videos
    Object.entries(videoRefs.current).forEach(([k, vid]) => {
      if (Number(k) !== active && vid) vid.pause();
    });
  }, [active, muted, items]);

  const current = items[active];

  return (
    <div className="flex flex-col gap-3">
      {/* Main slot */}
      <div
        className="relative w-full overflow-hidden"
        style={{ backgroundColor: '#FFFFFF', aspectRatio: '1 / 1', borderRadius: '20px' }}
      >
        {items.map((m, i) =>
          m.type === 'video' ? (
            <video
              key={i}
              ref={(el) => (videoRefs.current[i] = el)}
              src={m.src}
              poster={m.poster}
              autoPlay
              muted
              loop
              playsInline
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
                active === i ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
            />
          ) : (
            <img
              key={i}
              src={m.src}
              alt={m.alt || 'product'}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
                active === i ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
            />
          )
        )}

        {/* Mute/unmute when active slide is video */}
        {current?.type === 'video' && (
          <button
            onClick={() => setMuted((m) => !m)}
            aria-label={muted ? 'Unmute' : 'Mute'}
            className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center btn-hover"
          >
            {muted ? <VolumeX size={18} color="#2D5A3D" /> : <Volume2 size={18} color="#2D5A3D" />}
          </button>
        )}

        {/* "Video" pill at top-left */}
        {current?.type === 'video' && (
          <span
            className="absolute top-4 left-4 inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold tracking-wide"
            style={{ backgroundColor: 'rgba(45,90,61,0.92)', color: '#FFFFFF' }}
          >
            <Play size={12} fill="#FFFFFF" />
            VIDEO
          </span>
        )}
      </div>

      {/* Horizontal thumbnails */}
      <div className="flex gap-2 md:gap-3 overflow-x-auto scrollbar-thin pb-1">
        {items.map((m, i) => (
          <button
            key={i}
            onClick={() => setActive(i)}
            className="shrink-0 overflow-hidden transition-all relative"
            style={{
              width: '88px',
              height: '88px',
              borderRadius: '14px',
              border: active === i ? '2px solid #2D5A3D' : '2px solid transparent',
              opacity: active === i ? 1 : 0.85,
            }}
            aria-label={`Show slide ${i + 1}`}
          >
            <img
              src={m.type === 'video' ? m.poster : m.src}
              alt=""
              className="w-full h-full object-cover"
            />
            {m.type === 'video' && (
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="w-6 h-6 rounded-full bg-black/55 backdrop-blur-sm flex items-center justify-center">
                  <Play size={11} fill="#FFFFFF" color="#FFFFFF" />
                </span>
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

export default ProductGallery;
